import React, { ChangeEvent, useEffect, useState } from 'react';
import css from './ProjectCreationSection.module.css';
import { useProjectStore } from '../../../Stores/ProjectStore';
import { useGoogleMapViewModel } from '@viewModels/GoogleMap/GoogleMapViewModel';
import { CustomProjectInfoViewModel } from '@viewModels/CustomProjectInfoViewModel';
import { useParams } from 'react-router-dom';
import { StepsViewModeEnum } from '@shared/Enums/StepsViewModeEnum';
import { useMediaType } from '@shared/Hooks/useMediaTypeHook';
import TextInput from '@shared/Ui/CustomInputs/TextInput/TextInput';
import { BaseProjectEntity } from '@entities/BaseProject/Types';
import DateInput from '@shared/Ui/CustomInputs/DateInput/DateInput';
import { TOMORROW_DATE } from '@shared/Constants/TomorrowDate';
import GoogleMapSuggestionsInput from '@shared/Ui/CustomInputs/GoogleMapSuggestionsInput/GoogleMapSuggestionsInput';
import BasicButton from '@shared/Ui/Buttons/BasicButton/BasicButton';
import { MapModal } from '@shared/Features/ProjectCreationSection/MapModal/MapModal';

const ProjectCreationSection: React.FC = () => {
    const {
        address,
        setAddress,
        addressSuggestions,
        setAddressSuggestions,
        setMarkerPosition,
        isWithinCountry,
        getAddressSuggestions,
        geocodeAddress,
        getAddressFromLatLng,
    } = useGoogleMapViewModel();

    const [isDesktop] = useMediaType();

    const { customProject } = useProjectStore();
    const projectViewModel = new CustomProjectInfoViewModel();
    const { viewMode } = useParams();

    const [isMapModalOpen, setIsMapModalOpen] = useState(false);
    const handleOpenMapModal = () => setIsMapModalOpen(true);
    const handleCloseMapModal = () => setIsMapModalOpen(false);

    useEffect(() => {
        const loadProjectAddress = async () => {
            if (
                !customProject.address ||
                !customProject.address.latitude ||
                !customProject.address.longitude
            ) {
                return;
            }
            setMarkerPosition({
                lat: parseFloat(customProject.address.latitude),
                lng: parseFloat(customProject.address.longitude),
            });
            getAddressFromLatLng(
                parseFloat(customProject.address.latitude),
                parseFloat(customProject.address.longitude)
            );
        };

        loadProjectAddress();
    }, []);

    const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setAddress(value);
        getAddressSuggestions(value);
    };

    const handleAddressSelect = async (suggestion: string) => {
        setAddress(suggestion);
        setAddressSuggestions([]);

        const location = await geocodeAddress(suggestion);
        if (location) {
            const isValidLocation = await isWithinCountry(
                location.lat,
                location.lng
            );
            if (isValidLocation) {
                setMarkerPosition(location);
            } else {
                setMarkerPosition(null);
            }
        } else {
            setMarkerPosition(null);
        }
    };

    const handleChangeProjectField =
        (key: keyof BaseProjectEntity) =>
        (e: ChangeEvent<HTMLInputElement>) => {
            projectViewModel.updateCustomProject(key, e.target.value);
        };

    return (
        <div className={css.googleMapAdress}>
            {isDesktop && <div className={css.header}>Project details</div>}

            <div className={css.inputsSection}>
                <div className={css.inputWrapper}>
                    <span>Project name:</span>
                    <TextInput
                        value={customProject.title}
                        onChange={handleChangeProjectField('title')}
                        placeholder={'Enter name'}
                        disabled={viewMode === StepsViewModeEnum.PRESENTATION}
                        maxLength={128}
                    />
                </div>

                <div className={css.inputWrapper}>
                    <span>Target date:</span>
                    <DateInput
                        value={customProject.targetDate}
                        onChange={handleChangeProjectField('targetDate')}
                        min={TOMORROW_DATE}
                        showIcon={true}
                        disabled={viewMode === StepsViewModeEnum.PRESENTATION}
                    />
                </div>

                <div className={css.inputWrapper}>
                    <span>Your project address:</span>
                    <GoogleMapSuggestionsInput
                        value={address}
                        onChange={handleAddressChange}
                        placeholder={`Enter project address`}
                        suggestions={addressSuggestions}
                        onSelectSuggestion={handleAddressSelect}
                        disabled={viewMode === StepsViewModeEnum.PRESENTATION}
                        disableSuggestions={isMapModalOpen}
                    />
                    <BasicButton
                        className={css.selectOnMapBtn}
                        onClick={handleOpenMapModal}
                        disabled={viewMode === StepsViewModeEnum.PRESENTATION}
                    >
                        Select on map
                    </BasicButton>
                </div>
            </div>
            {isMapModalOpen && <MapModal onClose={handleCloseMapModal} />}
        </div>
    );
};

export default ProjectCreationSection;
